import {
  BLOCK_CATEGORIES,
  BLOCK_CATEGORY_LABELS,
  type BlockBuildOptions,
  type BlockCategory,
  blockSearchTerms,
  type LibraryBlock,
  type LibraryItem,
  searchList,
} from "@cva/shared";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Blocks, Search, X } from "lucide-react";
import { type ReactNode, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import type { BlockFilter } from "./BlocksPanel";
import { LibraryBlockTile, SavedBlockTile } from "./block-tiles";

type SavedItem = Extract<LibraryItem, { kind: "element" }>;

/** Width of a tile in the browser; the height follows the page format. */
const TILE_WIDTH = 204;

/**
 * All building blocks at a glance, nearly fullscreen: categories with counts
 * on the left, a large grid on the right (sections per category under
 * "Alle"). Hovering plays a tile's animation, a click inserts the block like
 * in the panel and closes the browser. Tiles build and draw only once they
 * scroll into view.
 */
export function BlocksBrowser({
  onClose,
  initialFilter,
  initialQuery,
  blocks,
  savedItems,
  buildOptions,
  frame,
  kind,
  insertBlock,
  insertSaved,
}: {
  onClose: () => void;
  initialFilter: BlockFilter;
  initialQuery: string;
  blocks: readonly LibraryBlock[];
  savedItems: readonly SavedItem[];
  buildOptions: Omit<BlockBuildOptions, "at">;
  frame: { width: number; height: number };
  kind: "design" | "video";
  insertBlock: (block: LibraryBlock) => void;
  insertSaved: (item: SavedItem) => void;
}) {
  const [filter, setFilter] = useState<BlockFilter>(initialFilter);
  const [query, setQuery] = useState(initialQuery);
  const search = useRef<HTMLInputElement>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const found = useMemo(() => searchList(blocks, query, blockSearchTerms), [blocks, query]);
  const foundSaved = useMemo(
    () => searchList(savedItems, query, (item) => [item.name]),
    [savedItems, query],
  );
  const categories = BLOCK_CATEGORIES.filter((category) =>
    blocks.some((block) => block.category === category),
  );
  const count = (category: BlockCategory) =>
    found.filter((block) => block.category === category).length;
  // Tiles in the shape of the page, within sensible bounds (reels are tall, videos wide).
  const tileHeight = Math.round(
    Math.min(280, Math.max(140, (TILE_WIDTH * frame.height) / frame.width)),
  );
  const pick = (next: BlockFilter) => {
    setFilter(next);
    scroller.current?.scrollTo({ top: 0 });
  };
  const inserted = (title: string) => {
    toast.success(`„${title}“ eingefügt`);
    onClose();
  };
  const trimmed = query.trim();
  const showSaved = filter === "all" || filter === "mine";
  const sections = BLOCK_CATEGORIES.filter(
    (category) => (filter === "all" || filter === category) && count(category) > 0,
  );
  const nothing =
    (filter === "mine" ? foundSaved.length === 0 : sections.length === 0) &&
    !(showSaved && foundSaved.length > 0);

  return (
    <DialogPrimitive.Root open onOpenChange={(open) => !open && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-zinc-950/50 backdrop-blur-[2px]" />
        <DialogPrimitive.Content
          className="fixed inset-3 z-50 flex flex-col overflow-hidden rounded-xl bg-white shadow-2xl focus:outline-none md:inset-6"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            search.current?.focus();
            search.current?.select();
          }}
        >
          <div className="flex shrink-0 items-center gap-4 border-b border-zinc-200 px-5 py-3">
            <div className="min-w-0">
              <DialogPrimitive.Title className="text-lg font-semibold text-zinc-900">
                Bausteine
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="text-xs text-zinc-500">
                {kind === "video"
                  ? "Zum Anschauen mit der Maus darüberfahren, Klick fügt am Abspielkopf ein."
                  : "Klick fügt den Baustein als Gruppe in die Seite ein."}
              </DialogPrimitive.Description>
            </div>
            <div className="relative ml-auto w-full max-w-sm">
              <Search className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-zinc-400" />
              <input
                ref={search}
                type="search"
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  scroller.current?.scrollTo({ top: 0 });
                }}
                placeholder="Suchen, z. B. Abo, Preis, Collage"
                aria-label="Bausteine durchsuchen"
                className="h-9 w-full rounded-lg border border-zinc-200 pr-3 pl-9 text-sm focus:border-brand-500 focus:outline-none"
              />
            </div>
            <DialogPrimitive.Close
              className="rounded-md p-1.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
              aria-label="Schließen"
              title="Schließen (Esc)"
            >
              <X className="size-5" />
            </DialogPrimitive.Close>
          </div>
          <div className="flex min-h-0 flex-1">
            <nav
              aria-label="Kategorien"
              className="w-52 shrink-0 space-y-0.5 overflow-y-auto border-r border-zinc-100 bg-zinc-50/60 p-3"
            >
              <CategoryButton
                active={filter === "all"}
                onClick={() => pick("all")}
                count={found.length + foundSaved.length}
              >
                Alle
              </CategoryButton>
              <CategoryButton
                active={filter === "mine"}
                onClick={() => pick("mine")}
                count={foundSaved.length}
              >
                Meine Bausteine
              </CategoryButton>
              <div className="my-2 border-t border-zinc-200" />
              {categories.map((category) => (
                <CategoryButton
                  key={category}
                  active={filter === category}
                  onClick={() => pick(category)}
                  count={count(category)}
                >
                  {BLOCK_CATEGORY_LABELS[category]}
                </CategoryButton>
              ))}
            </nav>
            <div ref={scroller} className="min-w-0 flex-1 space-y-8 overflow-y-auto p-5">
              {nothing ? (
                <p className="rounded-lg bg-zinc-50 p-4 text-sm text-zinc-600">
                  {trimmed
                    ? `Kein Baustein passt zu „${trimmed}“.`
                    : filter === "mine"
                      ? "Noch keine eigenen Bausteine. Wähle im Editor Elemente aus und speichere sie mit „Als Baustein speichern“."
                      : "Hier gibt es keine Bausteine."}
                </p>
              ) : null}
              {showSaved && foundSaved.length > 0 ? (
                <Section title="Meine Bausteine" count={foundSaved.length}>
                  {foundSaved.map((item) => (
                    <SavedBlockTile
                      key={item.id}
                      item={item}
                      frame={frame}
                      kind={kind}
                      width={TILE_WIDTH}
                      height={tileHeight}
                      large
                      onInsert={() => {
                        insertSaved(item);
                        inserted(item.name);
                      }}
                    />
                  ))}
                </Section>
              ) : null}
              {filter !== "mine"
                ? sections.map((category) => (
                    <Section
                      key={category}
                      title={BLOCK_CATEGORY_LABELS[category]}
                      count={count(category)}
                    >
                      {found
                        .filter((block) => block.category === category)
                        .map((block) => (
                          <LibraryBlockTile
                            key={block.id}
                            block={block}
                            buildOptions={buildOptions}
                            width={TILE_WIDTH}
                            height={tileHeight}
                            large
                            onInsert={() => {
                              insertBlock(block);
                              inserted(block.title);
                            }}
                          />
                        ))}
                    </Section>
                  ))
                : null}
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function CategoryButton({
  active,
  onClick,
  count,
  children,
}: {
  active: boolean;
  onClick: () => void;
  count: number;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? "true" : undefined}
      className={cn(
        "flex w-full items-center justify-between gap-2 rounded-md px-2.5 py-1.5 text-left text-sm",
        active
          ? "bg-zinc-900 font-medium text-white"
          : "text-zinc-700 hover:bg-zinc-200/60 hover:text-zinc-900",
      )}
    >
      <span className="truncate">{children}</span>
      <span
        className={cn("shrink-0 text-xs tabular-nums", active ? "text-zinc-300" : "text-zinc-400")}
      >
        {count}
      </span>
    </button>
  );
}

function Section({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: ReactNode;
}) {
  return (
    <section className="space-y-3">
      <h3 className="flex items-baseline gap-2 text-sm font-semibold text-zinc-900">
        {title === "Meine Bausteine" ? (
          <Blocks className="size-4 self-center text-zinc-400" />
        ) : null}
        {title}
        <span className="text-xs font-normal text-zinc-400">{count}</span>
      </h3>
      <div
        className="grid gap-4"
        // Fixed tile width (previews scale to it); spare room goes between the columns.
        style={{
          gridTemplateColumns: `repeat(auto-fill, ${TILE_WIDTH}px)`,
          justifyContent: "space-between",
        }}
      >
        {children}
      </div>
    </section>
  );
}
